import { FC, useEffect, useMemo, useState } from "react";
import { ReportSection } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { NoteEditor } from "~/features/notes/components/NoteEditor";
import { htmlToMarkdown } from "~/utils/htmlToMarkdown";
import logger from "~/utils/logger";

interface EditReportSectionDialogProps {
  section: ReportSection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (content: string) => Promise<void>;
}

export const EditReportSectionDialog: FC<EditReportSectionDialogProps> = ({
  section,
  open,
  onOpenChange,
  onSave,
}) => {
  const [content, setContent] = useState(section.content ?? "");
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setContent(section.content ?? "");
    }
  }, [open, section.id, section.content]);

  const isContentEmpty = useMemo(() => {
    const markdown = htmlToMarkdown(content);
    return markdown.trim() === "";
  }, [content]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const markdown = htmlToMarkdown(content);
      await onSave(markdown);
      onOpenChange(false);
    } catch (error) {
      logger.error("Failed to save section:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setContent(section.content ?? "");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Section: {section.title}</DialogTitle>
          <DialogDescription>
            Make changes to the section content. The content will be saved in
            markdown format.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <NoteEditor
            content={content}
            onChange={setContent}
            placeholder="Edit section content..."
            className="h-[500px] overflow-y-auto"
            showToolbar={true}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isContentEmpty}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
